/**
 * Binance Fiat REST API
 *
 * OpenAPI Specification for the Binance Fiat REST API
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type {
    AccountInfo,
    DepositResponse,
    FiatWithdrawResponse,
    GetFiatDepositWithdrawHistoryResponse,
    GetFiatPaymentsHistoryResponse,
    GetOrderDetailResponse,
} from '../types';

/**
 * FiatApi - axios parameter creator
 */
const FiatApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Submit deposit request, in this version, we only support BRL deposit via pix.
         *
         *
         *
         * For BRL deposit via pix, you need to place an order before making a transfer from your bank.
         *
         * Before calling this api, please make sure you have already completed your KYC or KYB, and already activated your fiat service on our website.
         *
         * Weight: 45000
         *
         * @summary Deposit(TRADE)
         * @param {string} currency
         * @param {string} apiPaymentMethod
         * @param {number | bigint} amount
         * @param {number | bigint} [recvWindow]
         * @param {object} [ext]
         *
         * @throws {RequiredError}
         */
        deposit: async (
            currency: string,
            apiPaymentMethod: string,
            amount: number | bigint,
            recvWindow?: number | bigint,
            ext?: object
        ): Promise<RequestArgs> => {
            // verify required parameter 'currency' is not null or undefined
            assertParamExists('deposit', 'currency', currency);
            // verify required parameter 'apiPaymentMethod' is not null or undefined
            assertParamExists('deposit', 'apiPaymentMethod', apiPaymentMethod);
            // verify required parameter 'amount' is not null or undefined
            assertParamExists('deposit', 'amount', amount);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            if (currency !== undefined && currency !== null) {
                localVarBodyParameter['currency'] = currency;
            }

            if (apiPaymentMethod !== undefined && apiPaymentMethod !== null) {
                localVarBodyParameter['apiPaymentMethod'] = apiPaymentMethod;
            }

            if (amount !== undefined && amount !== null) {
                localVarBodyParameter['amount'] = amount;
            }

            if (ext !== undefined && ext !== null) {
                localVarBodyParameter['ext'] = ext;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/fiat/deposit',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Submit withdraw request, in this version, we support BRL,ARS,MXN withdrawal via bank_transfer.
         *
         * You need to call this api first, and call query order detail api in a loop to get the status of the order until this order is successful.
         *
         * Before calling this API, please ensure you have completed your KYC or KYB, activated your fiat service, and verified your destination bank account on our website.
         *
         * you need to bind your bank account on web/app before using the corresponding account number
         *
         * Weight: 45000
         *
         * @summary Fiat Withdraw(WITHDRAW)
         * @param {string} currency
         * @param {string} apiPaymentMethod
         * @param {number | bigint} amount
         * @param {AccountInfo} accountInfo
         * @param {number | bigint} [recvWindow]
         * @param {object} [ext]
         *
         * @throws {RequiredError}
         */
        fiatWithdraw: async (
            currency: string,
            apiPaymentMethod: string,
            amount: number | bigint,
            accountInfo: AccountInfo,
            recvWindow?: number | bigint,
            ext?: object
        ): Promise<RequestArgs> => {
            // verify required parameter 'currency' is not null or undefined
            assertParamExists('fiatWithdraw', 'currency', currency);
            // verify required parameter 'apiPaymentMethod' is not null or undefined
            assertParamExists('fiatWithdraw', 'apiPaymentMethod', apiPaymentMethod);
            // verify required parameter 'amount' is not null or undefined
            assertParamExists('fiatWithdraw', 'amount', amount);
            // verify required parameter 'accountInfo' is not null or undefined
            assertParamExists('fiatWithdraw', 'accountInfo', accountInfo);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            if (currency !== undefined && currency !== null) {
                localVarBodyParameter['currency'] = currency;
            }

            if (apiPaymentMethod !== undefined && apiPaymentMethod !== null) {
                localVarBodyParameter['apiPaymentMethod'] = apiPaymentMethod;
            }

            if (amount !== undefined && amount !== null) {
                localVarBodyParameter['amount'] = amount;
            }

            if (accountInfo !== undefined && accountInfo !== null) {
                localVarBodyParameter['accountInfo'] = accountInfo;
            }

            if (ext !== undefined && ext !== null) {
                localVarBodyParameter['ext'] = ext;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v2/fiat/withdraw',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get Fiat Deposit/Withdraw History
         *
         * If beginTime and endTime are not sent, the recent 30-day data will be returned.
         *
         * Weight: 45000
         *
         * @summary Get Fiat Deposit/Withdraw History (USER_DATA)
         * @param {string} transactionType 0-buy,1-sell
         * @param {number | bigint} [beginTime]
         * @param {number | bigint} [endTime]
         * @param {number | bigint} [page] default 1
         * @param {number | bigint} [rows] default 100, max 500
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getFiatDepositWithdrawHistory: async (
            transactionType: string,
            beginTime?: number | bigint,
            endTime?: number | bigint,
            page?: number | bigint,
            rows?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'transactionType' is not null or undefined
            assertParamExists('getFiatDepositWithdrawHistory', 'transactionType', transactionType);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};

            if (transactionType !== undefined && transactionType !== null) {
                localVarQueryParameter['transactionType'] = transactionType;
            }
            if (beginTime !== undefined && beginTime !== null) {
                localVarQueryParameter['beginTime'] = beginTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (page !== undefined && page !== null) {
                localVarQueryParameter['page'] = page;
            }
            if (rows !== undefined && rows !== null) {
                localVarQueryParameter['rows'] = rows;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/fiat/orders',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get Fiat Deposit/Withdraw History
         *
         * If beginTime and endTime are not sent, the recent 30-day data will be returned.
         * paymentMethod: Only when requesting payments history for buy (transactionType=0), response contains paymentMethod representing the way of purchase. Now we have:
         * Cash Balance
         * Credit Card
         * Online Banking
         * Bank Transfer
         *
         * Weight: 1
         *
         * @summary Get Fiat Payments History (USER_DATA)
         * @param {string} transactionType 0-buy,1-sell
         * @param {number | bigint} [beginTime]
         * @param {number | bigint} [endTime]
         * @param {number | bigint} [page] default 1
         * @param {number | bigint} [rows] default 100, max 500
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getFiatPaymentsHistory: async (
            transactionType: string,
            beginTime?: number | bigint,
            endTime?: number | bigint,
            page?: number | bigint,
            rows?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'transactionType' is not null or undefined
            assertParamExists('getFiatPaymentsHistory', 'transactionType', transactionType);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};

            if (transactionType !== undefined && transactionType !== null) {
                localVarQueryParameter['transactionType'] = transactionType;
            }
            if (beginTime !== undefined && beginTime !== null) {
                localVarQueryParameter['beginTime'] = beginTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (page !== undefined && page !== null) {
                localVarQueryParameter['page'] = page;
            }
            if (rows !== undefined && rows !== null) {
                localVarQueryParameter['rows'] = rows;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/fiat/payments',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get Order Detail
         *
         * Before calling this api, please make sure you have already completed your KYC or KYB, and already activated your fiat service on our website.
         *
         * Weight: 1
         *
         * @summary Get Order Detail(USER_DATA)
         * @param {string} orderNo order id retrieved from the api call of withdrawal
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getOrderDetail: async (
            orderNo: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'orderNo' is not null or undefined
            assertParamExists('getOrderDetail', 'orderNo', orderNo);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};

            if (orderNo !== undefined && orderNo !== null) {
                localVarQueryParameter['orderNo'] = orderNo;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/fiat/get-order-detail',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * FiatApi - interface
 * @interface FiatApi
 */
export interface FiatApiInterface {
    /**
     * Submit deposit request, in this version, we only support BRL deposit via pix.
     *
     *
     *
     * For BRL deposit via pix, you need to place an order before making a transfer from your bank.
     *
     * Before calling this api, please make sure you have already completed your KYC or KYB, and already activated your fiat service on our website.
     *
     * Weight: 45000
     *
     * @summary Deposit(TRADE)
     * @param {DepositRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApiInterface
     */
    deposit(requestParameters: DepositRequest): Promise<RestApiResponse<DepositResponse>>;
    /**
     * Submit withdraw request, in this version, we support BRL,ARS,MXN withdrawal via bank_transfer.
     *
     * You need to call this api first, and call query order detail api in a loop to get the status of the order until this order is successful.
     *
     * Before calling this API, please ensure you have completed your KYC or KYB, activated your fiat service, and verified your destination bank account on our website.
     *
     * you need to bind your bank account on web/app before using the corresponding account number
     *
     * Weight: 45000
     *
     * @summary Fiat Withdraw(WITHDRAW)
     * @param {FiatWithdrawRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApiInterface
     */
    fiatWithdraw(
        requestParameters: FiatWithdrawRequest
    ): Promise<RestApiResponse<FiatWithdrawResponse>>;
    /**
     * Get Fiat Deposit/Withdraw History
     *
     * If beginTime and endTime are not sent, the recent 30-day data will be returned.
     *
     * Weight: 45000
     *
     * @summary Get Fiat Deposit/Withdraw History (USER_DATA)
     * @param {GetFiatDepositWithdrawHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApiInterface
     */
    getFiatDepositWithdrawHistory(
        requestParameters: GetFiatDepositWithdrawHistoryRequest
    ): Promise<RestApiResponse<GetFiatDepositWithdrawHistoryResponse>>;
    /**
     * Get Fiat Deposit/Withdraw History
     *
     * If beginTime and endTime are not sent, the recent 30-day data will be returned.
     * paymentMethod: Only when requesting payments history for buy (transactionType=0), response contains paymentMethod representing the way of purchase. Now we have:
     * Cash Balance
     * Credit Card
     * Online Banking
     * Bank Transfer
     *
     * Weight: 1
     *
     * @summary Get Fiat Payments History (USER_DATA)
     * @param {GetFiatPaymentsHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApiInterface
     */
    getFiatPaymentsHistory(
        requestParameters: GetFiatPaymentsHistoryRequest
    ): Promise<RestApiResponse<GetFiatPaymentsHistoryResponse>>;
    /**
     * Get Order Detail
     *
     * Before calling this api, please make sure you have already completed your KYC or KYB, and already activated your fiat service on our website.
     *
     * Weight: 1
     *
     * @summary Get Order Detail(USER_DATA)
     * @param {GetOrderDetailRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApiInterface
     */
    getOrderDetail(
        requestParameters: GetOrderDetailRequest
    ): Promise<RestApiResponse<GetOrderDetailResponse>>;
}

/**
 * Request parameters for deposit operation in FiatApi.
 * @interface DepositRequest
 */
export interface DepositRequest {
    /**
     *
     * @type {string}
     * @memberof FiatApiDeposit
     */
    readonly currency: string;

    /**
     *
     * @type {string}
     * @memberof FiatApiDeposit
     */
    readonly apiPaymentMethod: string;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiDeposit
     */
    readonly amount: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiDeposit
     */
    readonly recvWindow?: number | bigint;

    /**
     *
     * @type {object}
     * @memberof FiatApiDeposit
     */
    readonly ext?: object;
}

/**
 * Request parameters for fiatWithdraw operation in FiatApi.
 * @interface FiatWithdrawRequest
 */
export interface FiatWithdrawRequest {
    /**
     *
     * @type {string}
     * @memberof FiatApiFiatWithdraw
     */
    readonly currency: string;

    /**
     *
     * @type {string}
     * @memberof FiatApiFiatWithdraw
     */
    readonly apiPaymentMethod: string;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiFiatWithdraw
     */
    readonly amount: number | bigint;

    /**
     *
     * @type {AccountInfo}
     * @memberof FiatApiFiatWithdraw
     */
    readonly accountInfo: AccountInfo;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiFiatWithdraw
     */
    readonly recvWindow?: number | bigint;

    /**
     *
     * @type {object}
     * @memberof FiatApiFiatWithdraw
     */
    readonly ext?: object;
}

/**
 * Request parameters for getFiatDepositWithdrawHistory operation in FiatApi.
 * @interface GetFiatDepositWithdrawHistoryRequest
 */
export interface GetFiatDepositWithdrawHistoryRequest {
    /**
     * 0-buy,1-sell
     * @type {string}
     * @memberof FiatApiGetFiatDepositWithdrawHistory
     */
    readonly transactionType: string;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiGetFiatDepositWithdrawHistory
     */
    readonly beginTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiGetFiatDepositWithdrawHistory
     */
    readonly endTime?: number | bigint;

    /**
     * default 1
     * @type {number | bigint}
     * @memberof FiatApiGetFiatDepositWithdrawHistory
     */
    readonly page?: number | bigint;

    /**
     * default 100, max 500
     * @type {number | bigint}
     * @memberof FiatApiGetFiatDepositWithdrawHistory
     */
    readonly rows?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiGetFiatDepositWithdrawHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getFiatPaymentsHistory operation in FiatApi.
 * @interface GetFiatPaymentsHistoryRequest
 */
export interface GetFiatPaymentsHistoryRequest {
    /**
     * 0-buy,1-sell
     * @type {string}
     * @memberof FiatApiGetFiatPaymentsHistory
     */
    readonly transactionType: string;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiGetFiatPaymentsHistory
     */
    readonly beginTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiGetFiatPaymentsHistory
     */
    readonly endTime?: number | bigint;

    /**
     * default 1
     * @type {number | bigint}
     * @memberof FiatApiGetFiatPaymentsHistory
     */
    readonly page?: number | bigint;

    /**
     * default 100, max 500
     * @type {number | bigint}
     * @memberof FiatApiGetFiatPaymentsHistory
     */
    readonly rows?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiGetFiatPaymentsHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getOrderDetail operation in FiatApi.
 * @interface GetOrderDetailRequest
 */
export interface GetOrderDetailRequest {
    /**
     * order id retrieved from the api call of withdrawal
     * @type {string}
     * @memberof FiatApiGetOrderDetail
     */
    readonly orderNo: string;

    /**
     *
     * @type {number | bigint}
     * @memberof FiatApiGetOrderDetail
     */
    readonly recvWindow?: number | bigint;
}

/**
 * FiatApi - object-oriented interface
 * @class FiatApi
 */
export class FiatApi implements FiatApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = FiatApiAxiosParamCreator(configuration);
    }

    /**
     * Submit deposit request, in this version, we only support BRL deposit via pix.
     *
     *
     *
     * For BRL deposit via pix, you need to place an order before making a transfer from your bank.
     *
     * Before calling this api, please make sure you have already completed your KYC or KYB, and already activated your fiat service on our website.
     *
     * Weight: 45000
     *
     * @summary Deposit(TRADE)
     * @param {DepositRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<DepositResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApi
     * @see {@link https://developers.binance.com/docs/fiat/rest-api/Fiat-Deposit Binance API Documentation}
     */
    public async deposit(
        requestParameters: DepositRequest
    ): Promise<RestApiResponse<DepositResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.deposit(
            requestParameters?.currency,
            requestParameters?.apiPaymentMethod,
            requestParameters?.amount,
            requestParameters?.recvWindow,
            requestParameters?.ext
        );
        return sendRequest<DepositResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Submit withdraw request, in this version, we support BRL,ARS,MXN withdrawal via bank_transfer.
     *
     * You need to call this api first, and call query order detail api in a loop to get the status of the order until this order is successful.
     *
     * Before calling this API, please ensure you have completed your KYC or KYB, activated your fiat service, and verified your destination bank account on our website.
     *
     * you need to bind your bank account on web/app before using the corresponding account number
     *
     * Weight: 45000
     *
     * @summary Fiat Withdraw(WITHDRAW)
     * @param {FiatWithdrawRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<FiatWithdrawResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApi
     * @see {@link https://developers.binance.com/docs/fiat/rest-api/Fiat-Withdraw Binance API Documentation}
     */
    public async fiatWithdraw(
        requestParameters: FiatWithdrawRequest
    ): Promise<RestApiResponse<FiatWithdrawResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.fiatWithdraw(
            requestParameters?.currency,
            requestParameters?.apiPaymentMethod,
            requestParameters?.amount,
            requestParameters?.accountInfo,
            requestParameters?.recvWindow,
            requestParameters?.ext
        );
        return sendRequest<FiatWithdrawResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get Fiat Deposit/Withdraw History
     *
     * If beginTime and endTime are not sent, the recent 30-day data will be returned.
     *
     * Weight: 45000
     *
     * @summary Get Fiat Deposit/Withdraw History (USER_DATA)
     * @param {GetFiatDepositWithdrawHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetFiatDepositWithdrawHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApi
     * @see {@link https://developers.binance.com/docs/fiat/rest-api/Get-Fiat-Deposit-Withdraw-History Binance API Documentation}
     */
    public async getFiatDepositWithdrawHistory(
        requestParameters: GetFiatDepositWithdrawHistoryRequest
    ): Promise<RestApiResponse<GetFiatDepositWithdrawHistoryResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getFiatDepositWithdrawHistory(
                requestParameters?.transactionType,
                requestParameters?.beginTime,
                requestParameters?.endTime,
                requestParameters?.page,
                requestParameters?.rows,
                requestParameters?.recvWindow
            );
        return sendRequest<GetFiatDepositWithdrawHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get Fiat Deposit/Withdraw History
     *
     * If beginTime and endTime are not sent, the recent 30-day data will be returned.
     * paymentMethod: Only when requesting payments history for buy (transactionType=0), response contains paymentMethod representing the way of purchase. Now we have:
     * Cash Balance
     * Credit Card
     * Online Banking
     * Bank Transfer
     *
     * Weight: 1
     *
     * @summary Get Fiat Payments History (USER_DATA)
     * @param {GetFiatPaymentsHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetFiatPaymentsHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApi
     * @see {@link https://developers.binance.com/docs/fiat/rest-api/Get-Fiat-Payments-History Binance API Documentation}
     */
    public async getFiatPaymentsHistory(
        requestParameters: GetFiatPaymentsHistoryRequest
    ): Promise<RestApiResponse<GetFiatPaymentsHistoryResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getFiatPaymentsHistory(
            requestParameters?.transactionType,
            requestParameters?.beginTime,
            requestParameters?.endTime,
            requestParameters?.page,
            requestParameters?.rows,
            requestParameters?.recvWindow
        );
        return sendRequest<GetFiatPaymentsHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get Order Detail
     *
     * Before calling this api, please make sure you have already completed your KYC or KYB, and already activated your fiat service on our website.
     *
     * Weight: 1
     *
     * @summary Get Order Detail(USER_DATA)
     * @param {GetOrderDetailRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetOrderDetailResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof FiatApi
     * @see {@link https://developers.binance.com/docs/fiat/rest-api/Get-Order-Detail Binance API Documentation}
     */
    public async getOrderDetail(
        requestParameters: GetOrderDetailRequest
    ): Promise<RestApiResponse<GetOrderDetailResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getOrderDetail(
            requestParameters?.orderNo,
            requestParameters?.recvWindow
        );
        return sendRequest<GetOrderDetailResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }
}
